/* eslint-disable no-unused-vars */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  HeaderTitle,
  IconButton,
  LoaderWrapper,
  StyledContainer,
  TableCell,
  TableWrapper,
  TwoColumns,
} from '../../../services/settings/appStyle/global.style';
import { UserContainer } from './user.style';
import {
  DeleteConfirmationModal,
  DropdownButton,
  Loader,
  Table,
  IconButton as IconButtonWithText,
} from '../../../Components/uielements';
import { useUserContext } from '../../../hooks/UserContext';
import UserService from '../../../services/bioplus-services/user';
import Company from '../../../services/bioplus-services/company';
import { DeleteIcon, EditIcon, AddPlusIcon } from '../../../Components/icons';
import { InviteUserModal, PartnerUserEditModal } from './modals';
import { useCompany } from '../../../hooks/CompanyContext';
import { partnerOption, visitorOption } from './constants';
import { IntlMessages } from '../../../helper/langHelper';
import moment from 'moment';
// import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import { exportExcelFile } from '../../../helper/exportExcelHelper';
import { convertToUserTimeZoneDateTime } from '../../../helper/dataFormat';
import { WarningModal } from '../../../Components/uielements/deleteComfirmationModal/deleteConfirmationModal';

const NON_DELETABLE_ROLES = ['ADMIN', 'PARTNER', 'VISITOR'];

const PartnerRoles = () => {
  const [editUser, setEditUser] = useState();
  const [deleteUser, setDeleteUser] = useState();
  const userContext = useUserContext();
  const companyContext = useCompany();
  const companyFilter = companyContext?.company?._id;
  // const partnerId = userContext.company.companyId?.id;
  const partnerId = userContext.company?.partnerId;
  const roleId = userContext?.company?._id;

  const companyId = userContext?.company?.companyId?.id;

  const { company, userPermission } = userContext;
  const userEmail = company?.createdBy;
  const isUserAllowToManage = userPermission?.find((permission) => permission?.label === 'user');
  const canDownloadReport = !!isUserAllowToManage?.isReport;

  // Permission flag derived from the "user" permission object
  const canManageUser = !!isUserAllowToManage?.isManage;
  const canCreateUser = !!isUserAllowToManage?.isCreate;

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [name, setName] = useState();
  const [allUsers, setAllUsers] = useState(undefined);
  const [isAddUserModal, setIsAddUserModal] = useState();
  // const [isFetchAllUser, setIsFetchAllUser] = useState(false);
  const userTimeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  function extractData(dataArray) {
    if (dataArray) {
      const extractedData = dataArray?.map((item) => {
        return {
          Name: `${item?.userId?.firstName} ${item?.userId?.lastName}`,
          accountManager: item?.companyId?.name,
          email: item?.userId?.email,
          role:
            item.role === 'VISITOR'
              ? 'GUEST'
              : item.role === 'PARTNER'
              ? 'DISTRIBUTOR'
              : item?.label,
          mobile: item?.userId?.phone,
          lastLogin: item?.userId?.lastLogin
            ? convertToUserTimeZoneDateTime(item?.userId?.lastLogin, userTimeZone)
            : 'N/A',
          createdAt: moment(item?.createdAt)?.format('DD-MM-YYYY'),
        };
      });
      return { bioplusUsers: extractedData };
    }
  }

  useQuery(['allpartnerUsers'], () => UserService.listAllUserByPartner(partnerId), {
    enabled: !allUsers,
    onSuccess: (data) => {
      setAllUsers(data?.data ?? []);
    },
  });

  const usersQuery = useQuery(['partnerUsers', companyFilter, name, page, pageSize], () =>
    UserService.listByPartner(partnerId, name, page, pageSize, companyFilter)
  );
  const userTableData = usersQuery?.data?.data ?? [];
  const queryClient = useQueryClient();

  const deleteMutation = useMutation(
    (data) =>
      Company.removeUserFromCompany(data?.companyId?._id, data?.userId?._id, deleteUser?._id),
    {
      onSuccess: async () => {
        alert('Member deleted successfully.');
        await queryClient.invalidateQueries('partnerUsers');
      },
    }
  );

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };
  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  //   table coulmns
  const userColumns = useMemo(() => {
    const cols = [
      {
        Header: <IntlMessages id="table.col.name" />,
        accessor: 'userName',
        Cell: (cellData) => (
          <TableCell>
            {`${cellData?.row?.original?.userId?.firstName ?? '--'} ${
              cellData?.row?.original?.userId?.lastName ?? '--'
            }`}
          </TableCell>
        ),
        width: '16%',
      },
      {
        Header: <IntlMessages id="table.col.account.owner" />,
        accessor: 'companyId.name',
        width: '18%',
      },
      {
        Header: <IntlMessages id="table.col.email" />,
        accessor: 'userId.email',
        width: '18%',
      },
      {
        Header: <IntlMessages id="table.col.role" />,
        accessor: 'label',
        width: '10%',
      },
      {
        Header: <IntlMessages id="table.col.mobile.no" />,
        accessor: 'userId.phone',
      },
      {
        Header: <IntlMessages id="table.col.last.login" />,
        accessor: 'lastLogin',
        width: '15%',
        Cell: (cellData) => (
          <TableCell>
            {cellData?.row?.original?.userId?.lastLogin
              ? `${convertToUserTimeZoneDateTime(
                  cellData?.row?.original?.userId?.lastLogin,
                  userTimeZone
                )}`
              : 'N/A'}
          </TableCell>
        ),
      },
      {
        Header: <IntlMessages id="table.col.createdAt" />,
        accessor: 'createdAt',
        Cell: (cellData) => <TableCell>{moment(cellData?.value)?.format('DD-MM-YYYY')}</TableCell>,
        width: '12%',
      },
      {
        Header: <IntlMessages id="nav.subscriptionPlans" />,
        accessor: 'subscription',
        Cell: (cellData) => (
          <TableCell>
            {cellData?.row?.original?.subscription?.subscriptionId?.labelName
              ? cellData?.row?.original?.subscription?.subscriptionId?.labelName
              : 'Not Found'}
          </TableCell>
        ),
      },
    ];

    // Only show the Actions column (Edit/Delete) if the user has manage permission
    if (canManageUser) {
      cols.push({
        Header: <IntlMessages id="table.col.actions" defaultMessage="Actions" />,
        accessor: 'actions',
        isAction: true,
        width: '12%',
      });
    }

    return cols;
  }, [userTimeZone, canManageUser]);

  const handleClosed = () => {
    setEditUser(undefined);
  };

  const actionList = [
    (data) => (
      <IconButton onClick={() => setEditUser(data)}>
        <EditIcon />
      </IconButton>
    ),
    (data) => {
      const isDeleteDisabled =
        NON_DELETABLE_ROLES.includes(data?.role) || userEmail === data?.userId?.email;
      return (
        <IconButton
          onClick={() => !isDeleteDisabled && setDeleteUser(data)}
          disabled={isDeleteDisabled}
          style={isDeleteDisabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
        >
          <DeleteIcon style={{ width: '19px', height: '19px' }} />
        </IconButton>
      );
    },
  ];

  const handleDeleteUser = (data) => {
    deleteMutation.mutate(data);
    setDeleteUser(undefined);
  };
  const handleClosedAddUserModal = () => {
    setIsAddUserModal(false);
  };

  if (usersQuery.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  const excelFormatData = extractData(allUsers ?? []);
  const fileName = `Members-${moment().format('DD-MM-YYYY-hh.mm A')}.xlsx`;
  const fileNameCsv = `Members-${moment().format('DD-MM-YYYY-hh.mm A')}.csv`;
  const columnConfig = [
    { header: 'Name', key: 'Name', width: 30 },
    { header: 'Account Manager', key: 'accountManager', width: 30 },
    { header: 'Email', key: 'email', width: 30 },
    { header: 'Role', key: 'role', width: 20 },
    { header: 'Mobile No', key: 'mobile', width: 20 },
    { header: 'Last Login', key: 'lastLogin', width: 20 },
    { header: 'Created At', key: 'createdAt', width: 20 },
  ];

  const handleClickItem = (item) => {
    switch (item?.key) {
      case 'excel':
        exportExcelFile(excelFormatData, fileName, columnConfig);
        break;
      case 'pdf':
        break;
      case 'csv':
        exportExcelFile(excelFormatData, fileNameCsv, columnConfig);
        break;
      default:
        break;
    }
  };

  return (
    <StyledContainer>
      <UserContainer>
        <HeaderTitle>
          <IntlMessages id="roles.module.roles" />
        </HeaderTitle>{' '}
        <TwoColumns style={{ gap: '15px' }}>
          {canCreateUser && companyFilter && (
            <IconButtonWithText
              text={<IntlMessages id="button.text.add.user" />}
              icon={<AddPlusIcon />}
              onClick={() => {
                setIsAddUserModal(true);
              }}
            />
          )}
          {excelFormatData && canDownloadReport && (
            <DropdownButton
              noOfColoumns={1}
              buttonArray={[
                { key: 'excel', title: 'Export as Excel', icon: <CloudDownloadIcon /> },
                // { key: 'pdf', title: 'Export as PDF', icon: <PictureAsPdfIcon /> },
                { key: 'csv', title: 'Export as CSV', icon: <SimCardDownloadIcon /> },
              ]}
              onClick={handleClickItem}
            />
          )}
        </TwoColumns>
      </UserContainer>
      <TableWrapper>
        <Table
          data={userTableData}
          columns={userColumns}
          hasFooter={true}
          actions={actionList}
          page={page}
          pageSize={pageSize}
          prevPage={handlePreviousPage}
          nextPage={handleNextPage}
          total={usersQuery?.data?.total || 0}
        />
      </TableWrapper>
      {editUser && editUser !== undefined && (
        <PartnerUserEditModal
          onClose={handleClosed}
          data={editUser}
          userRole="partner"
          options={
            editUser?.role === 'VISITOR'
              ? visitorOption
              : editUser?.role === 'OWNER'
              ? partnerOption
              : []
          }
          invalidateQuery="partnerUsers"
        />
      )}
      {deleteUser && deleteUser?.role !== 'PARTNER' && deleteUser?.role !== 'ADMIN' && (
        <DeleteConfirmationModal
          onClose={() => {
            setDeleteUser(undefined);
          }}
          message={
            <IntlMessages
              id="delete.comfirmation.text.user"
              values={{
                user: ` ${deleteUser?.userId?.firstName} ${deleteUser?.userId?.lastName}`,
              }}
            />
          }
          onConfirm={() => handleDeleteUser(deleteUser)}
        />
      )}
      {deleteUser && (deleteUser.role === 'PARTNER' || deleteUser.role === 'ADMIN') && (
        <WarningModal
          onClose={() => setDeleteUser(undefined)}
          message={`The user '${deleteUser?.userId?.firstName} ${deleteUser?.userId?.lastName}' assigned the '${deleteUser?.label}' role, is not permitted to perform the delete action.`}
        />
      )}{' '}
      {isAddUserModal && (
        <InviteUserModal onClose={handleClosedAddUserModal} companyId={companyFilter} />
      )}
    </StyledContainer>
  );
};

export default PartnerRoles;
